/**
 * Generic in-memory CRUD store, backed by the REST API (PostgreSQL).
 *
 * Every module gets its own store via `createStore(key, seed, idPrefix, version, resource)`.
 * - Reads are synchronous from an in-memory cache so components stay simple.
 * - The cache starts EMPTY and is populated only by `refresh()` from the API —
 *   the database is the single source of truth (no localStorage, no seed fallback).
 * - When `resource` is given (e.g. '/invoices'), save/remove also write through
 *   to the API. The in-memory cache lives only for the current page session and
 *   is rebuilt from the DB on every load / refresh.
 */
import { message } from 'antd'
import { api, ApiError } from '@/services/api'

export interface Entity {
  id: string
}

export interface CrudStore<T extends Entity> {
  getAll: () => T[]
  get: (id: string) => T | undefined
  /** Create (no id / unknown id) or update (existing id). Returns the saved record. */
  save: (data: Partial<T> & { id?: string }) => T
  /**
   * Explicit create — ALWAYS POSTs a new row, never merges into / overwrites
   * an existing one, regardless of what `data.id` happens to be. Use this
   * (instead of `save`) for "create a brand-new record" flows where the id
   * comes from a generator (e.g. `generateCode()`) that isn't guaranteed
   * collision-free against the local cache — `save()` infers create-vs-update
   * purely from whether that id is already in the cache, so a stale/colliding
   * id there gets silently treated as an edit of the unrelated existing
   * record instead of a create. If the id genuinely already exists on the
   * server, this fails loudly (409) instead of overwriting it.
   */
  create: (data: Partial<T> & { id?: string }) => T
  remove: (id: string) => void
  /** Clear the in-memory cache (DB is untouched). */
  reset: () => T[]
  /** Pull fresh data from the API into the cache. */
  refresh: () => Promise<T[]>
  /** Window-event name fired on every change — subscribe to it for live updates. */
  eventName: string
  /** True once the first refresh() attempt has finished (success or failure).
   *  False for the brief window between mount and that first response — pages
   *  showing totals/tables from getAll() should key a loading spinner off
   *  this instead of rendering empty/₹0 while data is still in flight.
   *  Always true for stores with no `resource` (nothing to fetch). */
  readonly loaded: boolean
}

// Registry of API-backed stores so the app can hydrate them all at startup.
const apiBackedStores: Array<{ refresh: () => Promise<any> }> = []
export const refreshAllStores = (): Promise<any> =>
  Promise.all(apiBackedStores.map((s) => s.refresh()))

export function createStore<T extends Entity>(
  storageKey: string,
  _seed: T[],
  idPrefix: string,
  version = 1,
  /** Optional API resource path (e.g. '/invoices') to enable backend sync. */
  resource?: string,
): CrudStore<T> {
  const eventName = 'store-update-' + `${storageKey}__v${version}`

  // In-memory cache — the only client-side copy. Starts empty; filled by refresh().
  let cache: T[] = []
  // No resource ⇒ nothing this store will ever fetch, so it's trivially "loaded".
  let hasLoadedOnce = !resource

  const read = (): T[] => cache
  const write = (items: T[]) => { cache = items }
  const emit = (detail?: any) =>
    window.dispatchEvent(new CustomEvent(eventName, detail ? { detail } : undefined))

  const nextId = (): string =>
    `${idPrefix}${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).slice(2, 4).toUpperCase()}`

  // Serialise API writes (create/update/delete) so refresh() can wait for them
  // to land before re-fetching. Without this, a refresh fired right after a
  // save races the in-flight POST and overwrites the cache without the change
  // ("saved but missing after refresh" — more likely on slower/production networks).
  let writeChain: Promise<unknown> = Promise.resolve()
  const enqueueWrite = (run: () => Promise<unknown>): Promise<unknown> => {
    writeChain = writeChain.catch(() => {}).then(run)
    return writeChain
  }

  const store: CrudStore<T> = {
    eventName,
    get loaded() { return hasLoadedOnce },

    getAll: read,

    get: (id) => read().find((i) => i.id === id),

    save: (data) => {
      const previous = read()
      const items = [...previous]
      let result: T
      let isNew = false
      if (data.id) {
        const idx = items.findIndex((i) => i.id === data.id)
        if (idx !== -1) {
          result = { ...items[idx], ...data } as T
          items[idx] = result
          write(items)
        } else {
          result = { ...data, id: data.id } as T
          items.unshift(result)
          write(items)
          isNew = true
        }
      } else {
        result = { ...data, id: nextId() } as T
        items.unshift(result)
        write(items)
        isNew = true
      }
      emit({ item: result, isNew })
      // write-through: POST only for a genuinely new row — PUT for an edit.
      // Previously this always POSTed, so every edit of an existing record
      // (mark-as-read, approve/reject, record a payment, edit an agreement,
      // …) hit the backend's create() with a colliding id, got rejected with
      // 409, and was silently dropped by the .catch(console.error) below —
      // the cache showed the edit as saved, but a refresh reverted it.
      if (resource) {
        void enqueueWrite(() =>
          (isNew ? api.post(resource, result) : api.put(`${resource}/${result.id}`, result))
            .catch(err => {
              console.error(`[${storageKey}] Failed to save to the server:`, err)
              // The backend's 409 message (e.g. "Duplicate not allowed: another
              // record with the same name already exists.") is what the user
              // needs to see — a generic "check your connection" message here
              // hid the real reason the save was rejected. Also roll back the
              // optimistic cache change, since the row was never actually saved.
              write(previous)
              emit()
              if (err instanceof ApiError && err.status && err.status < 500 && err.message) {
                message.error(err.message)
              } else {
                message.error("Couldn't save your changes to the server — check your connection and try again.")
              }
              throw err
            })
        )
      }
      return result
    },

    create: (data) => {
      const previous = read()
      const id = data.id || nextId()
      const result = { ...data, id } as T
      // Always append + POST, even if something with this id is already in
      // the local cache — that would mean the id generator hasn't actually
      // guaranteed uniqueness, and the whole point of this method is to
      // never paper over that by quietly merging into the existing row (see
      // the interface doc above). If it genuinely collides server-side, the
      // backend's create() rejects with 409 and the catch below rolls this
      // optimistic add back — it can never silently overwrite another record.
      write([result, ...previous])
      emit({ item: result, isNew: true })
      if (resource) {
        void enqueueWrite(() =>
          api.post(resource, result).catch(err => {
            console.error(`[${storageKey}] Failed to create on the server:`, err)
            write(previous)
            emit()
            if (err instanceof ApiError && err.status && err.status < 500 && err.message) {
              message.error(err.message)
            } else {
              message.error("Couldn't save your changes to the server — check your connection and try again.")
            }
            throw err
          })
        )
      }
      return result
    },

    remove: (id) => {
      write(read().filter((i) => i.id !== id))
      emit()
      if (resource) void enqueueWrite(() => api.del(`${resource}/${id}`).catch(console.error)) // write-through
    },

    reset: () => {
      write([])
      emit()
      return []
    },

    refresh: async () => {
      if (!resource) return read()
      // The database is the source of truth: adopt exactly what it returns.
      try {
        // Let any pending create/update/delete finish first so the fetch below
        // reflects them, instead of racing and clobbering optimistic changes.
        await writeChain.catch(() => {})
        const data = await api.get<T[]>(resource)
        if (Array.isArray(data)) {
          write(data)
          emit()
        }
      } catch (err) {
        console.error('API Refresh Error:', err)
        /* leave cache as-is; no seed/localStorage fallback */
      } finally {
        // Flips once regardless of outcome — a spinner keyed on `loaded`
        // should clear after the first attempt, not hang forever if the
        // backend is offline.
        hasLoadedOnce = true
      }
      return read()
    },
  }

  if (resource) apiBackedStores.push(store)
  return store
}
